import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  useTheme,
  useMediaQuery,
  Avatar,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import BarChartIcon from "@mui/icons-material/BarChart";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import StarsIcon from "@mui/icons-material/Stars";
import { LineChart, Line, ResponsiveContainer } from "recharts";

// —— Netflix-ish tokens (visual only)
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.75)",
  border: "rgba(255,255,255,0.12)",
  red: "#E50914",
};

interface Props {
  totalChips: number;
  thisMonthChips: number;
  averageChips: number;
  bonusChips: number;
  thisWeekChips: number;
}

const formatNumber = (value: number) => value.toLocaleString();

const dummySparkline = [
  { value: 40 },
  { value: 55 },
  { value: 60 },
  { value: 48 },
  { value: 72 },
  { value: 66 },
];

export const StatsSummaryCard: React.FC<Props> = ({
  totalChips,
  thisMonthChips,
  averageChips,
  bonusChips,
  thisWeekChips,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const stats = [
    {
      label: "Total Earned",
      value: totalChips,
      icon: <EmojiEventsIcon />,
      color: theme.palette.primary.main,
    },
    {
      label: "This Week",
      value: thisWeekChips,
      icon: <BarChartIcon />,
      color: theme.palette.info.main,
    },
    {
      label: "This Month",
      value: thisMonthChips,
      icon: <BarChartIcon />,
      color: theme.palette.secondary.main,
    },
    {
      label: "Avg. per Booking",
      value: averageChips,
      icon: <MonetizationOnIcon />,
      color: theme.palette.success.main,
    },
    {
      label: "Yealry Earning",
      value: averageChips,
      icon: <MonetizationOnIcon />,
      color: theme.palette.success.main,
    },
    {
      label: "Bonus Chips",
      value: bonusChips,
      icon: <StarsIcon />,
      color: theme.palette.warning.main,
    },
  ];

  const gridSize = isMobile ? 12 : isTablet ? 6 : 2.4;

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 4,
        borderRadius: 4,
        backgroundColor: COLORS.panel,
        border: `1px solid ${COLORS.border}`,
        px: { xs: 2, sm: 3 },
        py: { xs: 2, sm: 3 },
        boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
      }}
    >
      <CardContent>
        <Grid container spacing={2}>
          {stats.map((s, idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Box
                p={2.5}
                borderRadius={3}
                sx={{
                  backgroundColor: "rgba(255,255,255,0.06)",
                  border: `1px solid ${COLORS.border}`,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: !isMobile && !isTablet
                      ? "0 8px 24px rgba(0,0,0,0.5)"
                      : undefined,
                    transform:
                      !isMobile && !isTablet ? "scale(1.015)" : undefined,
                    backgroundColor:
                      !isMobile && !isTablet
                        ? "rgba(255,255,255,0.1)"
                        : undefined,
                    borderColor: s.color,
                  },
                }}
              >
                <Box display="flex" alignItems="center" gap={2} mb={1.5}>
                  <Avatar
                    sx={{
                      bgcolor: s.color + "22",
                      color: s.color,
                      width: 38,
                      height: 38,
                      fontSize: "1.1rem",
                    }}
                  >
                    {s.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" sx={{ color: COLORS.muted }}>
                      {s.label}
                    </Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ color: COLORS.text }}>
                      {formatNumber(s.value)}
                    </Typography>
                  </Box>
                </Box>

                <Box height={36}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={dummySparkline}>
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke={s.color}
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};
